/**
 * Geodesic measurement on the WGS84 ellipsoid (§4.4) via Karney's algorithms.
 * The v3 prototype used spherical approximations; this replaces them.
 * All coordinates are [lat, lng].
 */
import { Geodesic } from 'geographiclib-geodesic';

const geod = Geodesic.WGS84;
export const SQM_TO_SQFT = 10.763910416709722;
export const M_TO_FT = 3.280839895013123;

export type LatLng = [number, number];

/** Ellipsoidal polygon area in square feet. Ring may be open or closed; orientation-free. */
export function polygonAreaSqFt(coords: LatLng[]): number {
  const ring = dedupeClosing(coords);
  if (ring.length < 3) return 0;
  const poly = geod.Polygon(false);
  for (const [lat, lng] of ring) poly.AddPoint(lat, lng);
  const { area } = poly.Compute(false, true);
  return Math.abs(area) * SQM_TO_SQFT;
}

/** Ellipsoidal polyline length in feet. */
export function lineLengthFt(coords: LatLng[]): number {
  if (coords.length < 2) return 0;
  let meters = 0;
  for (let i = 1; i < coords.length; i++) {
    const a = coords[i - 1]!;
    const b = coords[i]!;
    meters += geod.Inverse(a[0], a[1], b[0], b[1]).s12;
  }
  return meters * M_TO_FT;
}

/** Ellipsoidal perimeter of a ring in feet (closes the ring). */
export function perimeterFt(coords: LatLng[]): number {
  const ring = dedupeClosing(coords);
  if (ring.length < 2) return 0;
  return lineLengthFt([...ring, ring[0]!]);
}

/** drop an explicit closing point identical to the first */
function dedupeClosing(coords: LatLng[]): LatLng[] {
  if (coords.length > 1) {
    const first = coords[0]!;
    const last = coords[coords.length - 1]!;
    if (first[0] === last[0] && first[1] === last[1]) return coords.slice(0, -1);
  }
  return coords;
}

/** Net parcel area from Esri-style rings: largest ring is the outer boundary, the rest are holes. */
export function netRingsAreaSqFt(rings: LatLng[][]): number {
  if (!rings.length) return 0;
  const areas = rings.map(polygonAreaSqFt);
  const outer = Math.max(...areas);
  const holes = areas.reduce((s, a) => s + a, 0) - outer;
  return Math.max(0, outer - holes);
}

/** bbox [south, west, north, east] of a coordinate set */
export function bbox(coords: LatLng[]): [number, number, number, number] {
  let s = 90, w = 180, n = -90, e = -180;
  for (const [lat, lng] of coords) {
    if (lat < s) s = lat;
    if (lat > n) n = lat;
    if (lng < w) w = lng;
    if (lng > e) e = lng;
  }
  return [s, w, n, e];
}

/** guard oversized Overpass queries (§8): area of a bbox in acres, spherical is fine for a guard */
export function bboxAcres([s, w, n, e]: [number, number, number, number]): number {
  const R = 6371000;
  const rad = Math.PI / 180;
  const area = Math.abs((e - w) * rad * R * Math.cos(((s + n) / 2) * rad) * (n - s) * rad * R);
  return (area * SQM_TO_SQFT) / 43560;
}
